import React, { useRef } from 'react';
import { Monitor, Moon, Sun } from 'lucide-react';
import type { ThemePreference } from '../hooks/useTheme';

interface ThemeToggleProps {
  preference: ThemePreference;
  onChange: (preference: ThemePreference) => void;
  theme: 'light' | 'dark';
}

const OPTIONS: { value: ThemePreference; label: string; Icon: typeof Sun }[] = [
  { value: 'light', label: 'Light theme', Icon: Sun },
  { value: 'system', label: 'Match system theme', Icon: Monitor },
  { value: 'dark', label: 'Dark theme', Icon: Moon },
];

/**
 * Three-way theme switch implemented as an ARIA radio group:
 * one tab stop, arrow keys / Home / End move and select.
 */
export const ThemeToggle: React.FC<ThemeToggleProps> = ({ preference, onChange, theme }) => {
  const isDark = theme === 'dark';
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const selectedIndex = Math.max(0, OPTIONS.findIndex((o) => o.value === preference));

  const select = (index: number) => {
    const wrapped = (index + OPTIONS.length) % OPTIONS.length;
    onChange(OPTIONS[wrapped].value);
    buttonRefs.current[wrapped]?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    const keyMoves: Record<string, number> = {
      ArrowRight: selectedIndex + 1,
      ArrowDown: selectedIndex + 1,
      ArrowLeft: selectedIndex - 1,
      ArrowUp: selectedIndex - 1,
      Home: 0,
      End: OPTIONS.length - 1,
    };
    if (event.key in keyMoves) {
      event.preventDefault();
      select(keyMoves[event.key]);
    }
  };

  return (
    <div
      role="radiogroup"
      aria-label="Color theme"
      onKeyDown={onKeyDown}
      className={`flex items-center gap-0.5 p-1 rounded-xl border ${isDark ? 'border-slate-700 bg-slate-800/60' : 'border-slate-200 bg-white/70'}`}
    >
      {OPTIONS.map(({ value, label, Icon }, index) => {
        const checked = value === preference;
        return (
          <button
            key={value}
            ref={(el) => { buttonRefs.current[index] = el; }}
            type="button"
            role="radio"
            aria-checked={checked}
            aria-label={label}
            title={label}
            tabIndex={checked ? 0 : -1}
            onClick={() => onChange(value)}
            className={`p-1.5 rounded-lg transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
              checked
                ? isDark ? 'bg-slate-700 text-yellow-300 shadow' : 'bg-blue-50 text-blue-600 shadow'
                : 'text-slate-400 hover:text-blue-500'
            }`}
          >
            <Icon size={15} aria-hidden />
          </button>
        );
      })}
    </div>
  );
};

export default ThemeToggle;
